import { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { AppSidebar } from "./AppSidebar";
import { Breadcrumbs } from "./Breadcrumbs";
import { MobileMenu } from "./MobileMenu";
import { useIsMobile } from "@/modules/shared/hooks/use-mobile";
import { AssistantChat } from "@/modules/shared/components/assistant/AssistantChat";
import { useActivityTracker } from "@/modules/shared/hooks/useActivityTracker";
import { useTrialStatus } from "@/modules/shared/hooks/useTrialStatus";
import { cn } from "@/modules/shared/lib/utils";
import { useBranding } from "@/modules/shared/hooks/useBranding";

interface AppLayoutProps {
  children: ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const isMobile = useIsMobile();
  const location = useLocation();
  const { isPendingApproval, loading: trialLoading } = useTrialStatus();
  const { logoWhiteUrl, platformName } = useBranding();
  
  // Track user activity for "last seen" feature
  useActivityTracker();

  // Pipeline pages should fill viewport without scroll
  const isFullscreenPage = location.pathname.includes("/pipeline");

  return (
    <div className={cn("flex w-full bg-background", isFullscreenPage ? "h-screen overflow-hidden" : "min-h-screen")}>
      {/* Desktop Sidebar */}
      <div className="hidden md:block">
        <AppSidebar />
      </div>
      
      <main className={cn("flex-1 min-w-0", isFullscreenPage ? "flex flex-col overflow-hidden" : "overflow-x-hidden")}>
        {/* Mobile Header */}
        <div className="md:hidden sticky top-0 z-30 flex items-center gap-3 px-4 py-3 bg-sidebar border-b border-sidebar-border/30">
          <MobileMenu />
          <img src={logoWhiteUrl} alt={platformName} className="h-7" />
        </div>
        
        {!isFullscreenPage && (
          <div className="px-3 pt-3 md:px-6 md:pt-5 lg:px-8 lg:pt-6">
            <Breadcrumbs />
          </div>
        )}
        <div className={cn(
          isFullscreenPage 
            ? "flex-1 flex flex-col overflow-hidden" 
            : "px-3 pb-4 md:px-6 lg:px-8 md:pb-6"
        )}>
          {children}
        </div>
      </main>

      {/* AI Assistant - hide for pending approval orgs */}
      {!isPendingApproval && !trialLoading && <AssistantChat />}
    </div>
  );
}
